import type { SchedulingStepFormData } from '../../schemas/schedulingStepFormSchema';
import { getRecSelectionDateDefaults } from '../../utils/recSchedulingDates';

export type SchedulingEmr = 'kinnser' | 'roger';

/**
 * Build the patch applied when the user picks a visit type. Visit type drives
 * every other field: SOC pairs with "New admission" (or null when a new admission
 * cannot be created) and clears the ROC date; ROC pairs with "Current episode".
 *
 * `canSelectNewAdmission` must already account for both the matched episode's
 * discharge date and any open admission (see canSelectNewAdmissionForSoc) — when
 * false, SOC is left without a creation type rather than defaulting to "New admission".
 */
export function getVisitTypePatch(
  emr: SchedulingEmr,
  v: 'soc' | 'roc',
  canSelectNewAdmission: boolean
): Partial<SchedulingStepFormData> {
  const shared: Partial<SchedulingStepFormData> =
    v === 'soc'
      ? {
          kinnser_visit_type: 'soc',
          kinnser_creation_type: canSelectNewAdmission ? 'new_admission' : null,
          roc_date: null,
        }
      : {
          kinnser_visit_type: 'roc',
          kinnser_creation_type: 'current_episode',
        };

  if (emr === 'kinnser') {
    return {
      ...shared,
      kinnser_create_episode: v === 'soc',
    };
  }
  return shared;
}

/**
 * New-patient Roger scheduling header option: "Schedule SOC in Roger" vs
 * "Schedule REC in Roger" (recert onboarding for a patient migrated into Roger
 * mid-admission). Both set the same Roger scheduling flags; they differ only in
 * the shared `kinnser_visit_type` key ('rec' vs null — the new-patient SOC path
 * never persists a visit type).
 */
export type RogerNewPatientScheduleOption = 'soc' | 'rec';

/**
 * Patch applied when a new-patient Roger schedule checkbox ("Schedule SOC in
 * Roger" / "Schedule REC in Roger") is toggled. Checking one option implicitly
 * unchecks the other (they share the Roger flags and differ via
 * `kinnser_visit_type`). Unchecking clears the Roger scheduling flags and, for
 * REC, the visit type.
 *
 * Checking REC with no recert date yet also stamps the default date chain
 * (R = tomorrow, N = R+1, A = N-60) — see getRecSelectionDateDefaults for why
 * selection time is the only safe moment to overwrite the episode/SOC dates.
 */
export function getRogerNewPatientSchedulePatch(
  option: RogerNewPatientScheduleOption,
  isChecked: boolean,
  currentVisitType: string | null | undefined,
  currentRecertDate?: string | null
): Partial<SchedulingStepFormData> {
  if (isChecked) {
    return {
      roger_create_episode: true,
      push_to_roger: true,
      kinnser_create_episode: false,
      push_to_kinnser: false,
      kinnser_visit_type: option === 'rec' ? 'rec' : null,
      ...(option === 'rec'
        ? getRecSelectionDateDefaults(currentRecertDate)
        : {}),
    };
  }
  return {
    roger_create_episode: false,
    push_to_roger: false,
    ...(currentVisitType === 'rec' ? { kinnser_visit_type: null } : {}),
  };
}

/**
 * Extra keys to clear when the Kinnser schedule checkbox is CHECKED while a
 * Roger-only REC selection is active: REC is Roger-only, so the stale 'rec'
 * visit type must not leak into the Kinnser flow.
 */
export function getKinnserCheckClearsRecPatch(
  currentVisitType: string | null | undefined
): Partial<SchedulingStepFormData> {
  return currentVisitType === 'rec' ? { kinnser_visit_type: null } : {};
}

/**
 * The intake was already pushed/scheduled: the scheduling selection header
 * must render fully disabled so the (unchecked) sibling checkbox cannot
 * rewrite a pushed intake's visit type and EMR flags.
 */
export function isSchedulingPushCompleted(
  status: string | null | undefined
): boolean {
  return (
    status === 'pushed_to_roger' ||
    status === 'pushed_to_kinnser' ||
    status === 'scheduled'
  );
}
